import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Divider,
  useTheme,
  Avatar,
  TablePagination,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Header from "../components/Header";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../theme";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ReportPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Compound filter state
  const [compoundId, setCompoundId] = useState("all");
  const [compounds, setCompounds] = useState([]);

  useEffect(() => {
    fetchReports();
    fetchCompounds();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [compoundId, reports]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/maintenance-reports`);
      setReports(res.data.data || []);
    } catch (err) {
      setReports([]);
    }
    setLoading(false);
  };

  const fetchCompounds = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/getCompoundList`);
      setCompounds(res.data.compounds || []);
    } catch (e) {
      setCompounds([]);
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setOpenDialog(true);
  };

  // Compound info for specific selection
  const selectedCompoundInfo =
    compoundId !== "all" ? compounds.find((c) => c.id === compoundId) : null;

  // ---- FILTER & GROUP REPORTS ----
  const groupedReports = useMemo(() => {
    if (compoundId === "all") {
      // Group by compound id
      return reports.reduce((acc, r) => {
        const cmp = r.unit?.building?.compound;
        const cmpId = cmp?.id || "unknown";
        if (!acc[cmpId]) acc[cmpId] = { compound: cmp, reports: [] };
        acc[cmpId].reports.push(r);
        return acc;
      }, {});
    }
    // Else: filter for selected compound
    return {};
  }, [reports, compoundId]);

  const filteredReports = useMemo(() => {
    if (compoundId === "all") return reports;
    return reports.filter((r) => r.unit?.building?.compound?.id === compoundId);
  }, [reports, compoundId]);

  // Pagination logic (per group or per filter)
  let paginatedData = [];
  if (compoundId === "all") {
    // Flatten grouped for pagination across all
    paginatedData = filteredReports.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  } else {
    paginatedData = filteredReports.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }

  // Helper for rendering a compound info card
  const CompoundInfoCard = ({ compound }) =>
    compound ? (
      <Paper
        sx={{
          p: 2,
          mb: 2,
        }}
        elevation={3}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            align="center"
            src={compound.logo_url}
            sx={{ width: 72, height: 72, bgcolor: "white" }}
          >
            <Typography variant="h3" className="notranslate">
              {compound?.logo_url || compound.name[0].toUpperCase()}
            </Typography>
          </Avatar>

          <Box display="flex" justifyContent="center">
            <Typography variant="h5" mt={2} className="notranslate" fontWeight={600}>
              {compound.name}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="center">
            <Typography align="center" variant="h7" className="notranslate">
              ({compound.address})
            </Typography>
          </Box>
        </Box>
      </Paper>
    ) : null;

  // Table header: dynamic compound column
  const renderTableHead = () => (
    <TableHead>
      <TableRow>
        {compoundId === "all" && (
          <TableCell>
            <b>Compound</b>
          </TableCell>
        )}
        <TableCell>
          <b>Unit</b>
        </TableCell>
        <TableCell>
          <b>Building</b>
        </TableCell>
        <TableCell>
          <Box display="flex" justifyContent="center">
            Tenant
          </Box>
        </TableCell>
        <TableCell>
          <Box display="flex" justifyContent="center">
            Provider
          </Box>
        </TableCell>
        <TableCell>
          <b>Service</b>
        </TableCell>
        <TableCell>
          <b>Status</b>
        </TableCell>
        <TableCell>
          <b>Completed At</b>
        </TableCell>
        <TableCell>
          <b>Actions</b>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  // Table body
  const renderTableRows = () => {
    if (filteredReports.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={compoundId === "all" ? 9 : 8} align="center">
            No reports available.
          </TableCell>
        </TableRow>
      );
    }
    if (compoundId === "all") {
      return paginatedData.map((r) => (
        <TableRow key={r.id}>
          <TableCell className="notranslate">
            {r.unit?.building?.compound?.name || "-"}
          </TableCell>
          <TableCell className="notranslate">{r.unit?.name || "-"}</TableCell>
          <TableCell className="notranslate">
            {r.unit?.building?.name || "-"}
          </TableCell>
          <TableCell>
            <Tooltip
              className="notranslate"
              title={
                <Box>
                  <Typography>{r.tenant?.user.email}</Typography>
                  <Typography dir="ltr">{r.tenant?.user.phone}</Typography>
                </Box>
              }
            >
              <Box justifyItems={"center"} className="notranslate">
                <Avatar src={r.tenant?.user.avatar_url}>
                  {r.tenant?.user?.username?.[0] || "-"}
                </Avatar>
                <Typography>{r.tenant?.user.username}</Typography>
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell>
            <Tooltip
              className="notranslate"
              title={
                <Box className="notranslate">
                  <Typography>{r.serviceprovider?.user.email}</Typography>
                  <Typography dir="ltr">
                    {r.serviceprovider?.user.phone}
                  </Typography>
                </Box>
              }
            >
              <Box justifyItems={"center"} className="notranslate">
                <Avatar src={r.serviceprovider?.user.avatar_url}>
                  {r.serviceprovider?.user?.username?.[0] || "-"}
                </Avatar>
                <Typography>{r.serviceprovider?.user.username}</Typography>
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell>
            <Chip label={r.compoundServiceType?.name || "-"} color="info" />
          </TableCell>
          <TableCell>
            <Chip
              label={r.status}
              color={r.status === "COMPLETED" ? "success" : "info"}
              size="small"
            />
          </TableCell>
          <TableCell>
            {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "-"}
          </TableCell>
          <TableCell>
            <Tooltip title="View Report">
              <IconButton onClick={() => handleViewReport(r)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      ));
    } else {
      return paginatedData.map((r) => (
        <TableRow key={r.id}>
          <TableCell className="notranslate">{r.unit?.name || "-"}</TableCell>
          <TableCell className="notranslate">
            {r.unit?.building?.name || "-"}
          </TableCell>
          <TableCell>
            <Tooltip
              className="notranslate"
              title={
                <Box>
                  <Typography>{r.tenant?.user.email}</Typography>
                  <Typography dir="ltr">{r.tenant?.user.phone}</Typography>
                </Box>
              }
            >
              <Box justifyItems={"center"} className="notranslate">
                <Avatar src={r.tenant?.user.avatar_url}>
                  {r.tenant?.user?.username?.[0] || "-"}
                </Avatar>
                <Typography>{r.tenant?.user.username}</Typography>
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell>
            <Tooltip
              className="notranslate"
              title={
                <Box className="notranslate">
                  <Typography>{r.serviceprovider?.user.email}</Typography>
                  <Typography dir="ltr">
                    {r.serviceprovider?.user.phone}
                  </Typography>
                </Box>
              }
            >
              <Box justifyItems={"center"} className="notranslate">
                <Avatar src={r.serviceprovider?.user.avatar_url}>
                  {r.serviceprovider?.user?.username?.[0] || "-"}
                </Avatar>
                <Typography>{r.serviceprovider?.user.username}</Typography>
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell>
            <Chip label={r.compoundServiceType?.name || "-"} color="info" />
          </TableCell>
          <TableCell>
            <Chip
              label={r.status}
              color={r.status === "COMPLETED" ? "success" : "info"}
              size="small"
            />
          </TableCell>
          <TableCell>
            {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "-"}
          </TableCell>
          <TableCell>
            <Tooltip title="View Report">
              <IconButton onClick={() => handleViewReport(r)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      ));
    }
  };

  return (
    <Box className="main-container mt-4">
      <Header title="Report Management" />

      <Paper sx={{ p: 3, mt: 3, height: "80vh" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container columns={{ xs: 12, sm: 12, md: 12 }} spacing={3}>
            <Grid item size={{ xs: 12, sm: 5, md: 2 }}>
              <Grid container columns={{ xs: 12, sm: 12, md: 12 }} spacing={3}>
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl sx={{ minWidth: 240, mb: 3 }}>
                    <InputLabel>Compound</InputLabel>
                    <Select
                      value={compoundId}
                      onChange={(e) => setCompoundId(e.target.value)}
                      label="Compound"
                      className="notranslate"
                    >
                      <MenuItem value="all">All Compounds</MenuItem>
                      {compounds.map((cmp) => (
                        <MenuItem key={cmp.id} value={cmp.id} className="notranslate">
                          {cmp.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {compoundId !== "all" && selectedCompoundInfo && (
                  <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <CompoundInfoCard compound={selectedCompoundInfo} />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item size={{ xs: 12, sm: 7, md: 10 }}>
              <TableContainer
                sx={{
                  maxHeight: "65vh",
                  overflow: "auto",
                  minWidth: "100%",
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": {
                    height: "8px",
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f0f0f0",
                    borderRadius: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#c1c1c1",
                    borderRadius: "8px",
                    border: "2px solid #f0f0f0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#a0a0a0",
                  },
                }}
              >
                <Table stickyHeader>
                  {renderTableHead()}
                  <TableBody>{renderTableRows()}</TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredReports.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Report Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 2,
            px: 3,
            mb: -1,
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            Maintenance Report
          </Typography>
          <IconButton
            onClick={() => setOpenDialog(false)}
            edge="end"
            size="large"
            sx={{
              color: "#888",
              "&:hover": { color: "error.main", bgcolor: "transparent" },
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: { xs: 2, sm: 4 },
              background: colors.primary[700],
              borderRadius: 3,
              boxShadow: 3,
              maxWidth: 700,
              mx: "auto",
              my: 2,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              mb={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Box>
                <Typography variant="h6" color="text.secondary">
                  Unit
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={500}
                  className="notranslate"
                >
                  {selectedReport?.unit?.name || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" color="text.secondary">
                  Building
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={500}
                  className="notranslate"
                >
                  {selectedReport?.unit?.building?.name || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" color="text.secondary">
                  Service Type
                </Typography>
                <Typography variant="h5" fontWeight={500}>
                  {selectedReport?.compoundServiceType?.name || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" color="text.secondary">
                  Completed At
                </Typography>
                <Typography variant="h5">
                  {selectedReport?.updatedAt
                    ? new Date(selectedReport?.updatedAt).toLocaleString()
                    : "-"}
                </Typography>
              </Box>
            </Stack>

            {/* Section: Maintenance Request */}
            <Box
              sx={{
                background: colors.primary[800],
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                border: "1px solid #e4e9f2",
              }}
            >
              <Typography variant="h6" color="primary" mb={1}>
                Maintenance Request Description
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {selectedReport?.description || "-"}
              </Typography>
            </Box>

            {/* Section: Completion Report */}
            <Box
              sx={{
                background: colors.primary[800],
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                border: "1px solid #e4e9f2",
              }}
            >
              <Typography variant="h6" color={colors.greenAccent[100]} mb={1}>
                Completion Report
              </Typography>
              <Typography variant="h5">
                {selectedReport?.report || "-"}
              </Typography>
            </Box>

            {/* Section: Photos */}
            {selectedReport?.photos && selectedReport?.photos.length > 0 && (
              <Box
                sx={{
                  background: colors.primary[800],
                  borderRadius: 2,
                  p: 2.5,
                  mb: 1,
                  border: "1px solid #e4e9f2",
                }}
              >
                <Typography variant="h6" color={colors.blueAccent[200]} mb={2}>
                  Photos
                </Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  {selectedReport.photos.map((img, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 150,
                        height: 120,
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 2,
                        background: "#e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #e4e9f2",
                      }}
                    >
                      <img
                        src={img}
                        alt={`Report Photo ${i + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReportPage;
